import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { OrgType } from '../authentication/user-org-types.enum';
import { ApiResponse } from '../models/api-response';
import { PaginatedData } from '../models/paginated-data';
import { OrgUnitsWithPermissionsWrite } from '../models/site-dto';
import { UserDto, UserOverviewByOrganisationDto } from '../models/user-dto';

@Injectable({ providedIn: 'root' })
export class UserService {
  constructor(private httpClient: HttpClient) {}

  adminGet: (
    pageNumber: number,
    pageSize: number,
    orgType?: OrgType,
    orgId?: number,
    name?: string,
  ) => Observable<PaginatedData<UserDto>> = (
    pageNumber: number,
    pageSize: number,
    orgType?: OrgType,
    orgId?: number,
    name?: string,
  ) => {
    let params = new HttpParams();
    if (pageNumber) {
      params = params.append('pageNumber', pageNumber.toString());
    }
    if (pageSize) {
      params = params.append('pageSize', pageSize.toString());
    }
    if (orgType) {
      params = params.append('orgType', orgType);
    }
    if (orgId) {
      params = params.append('orgId', orgId.toString());
    }
    if (name) {
      params = params.append('name', name);
    }
    return this.httpClient
      .get(`${environment.backendUrl}/api/admin/v1/users`, { params })
      .pipe(map((response: PaginatedData<UserDto>) => response));
  };

  operatorGet: (pageNumber: number, pageSize: number, name?: string) => Observable<PaginatedData<UserDto>> = (
    pageNumber: number,
    pageSize: number,
    name?: string,
  ) => {
    let params = new HttpParams();
    if (pageNumber) {
      params = params.append('pageNumber', pageNumber.toString());
    }
    if (pageSize) {
      params = params.append('pageSize', pageSize.toString());
    }
    if (name) {
      params = params.append('name', name);
    }
    return this.httpClient
      .get(`${environment.backendUrl}/api/operator/v1/users/paginated`, { params })
      .pipe(map((response: PaginatedData<UserDto>) => response));
  };

  operatorGetAllUsers(): Observable<UserDto[]> {
    return this.httpClient.get<UserDto[]>(`${environment.backendUrl}/api/operator/v1/users`);
  }

  // user details

  adminGetById(userId: number): Observable<UserDto> {
    return this.httpClient.get<UserDto>(`${environment.backendUrl}/api/admin/v1/users/${userId}`);
  }

  operatorGetById(userId: number): Observable<UserDto> {
    return this.httpClient.get<UserDto>(`${environment.backendUrl}/api/operator/v1/users/${userId}`);
  }

  // user CRUD

  adminCreateUser(value: any): Observable<UserDto> {
    return this.httpClient.post<UserDto>(`${environment.backendUrl}/api/admin/v1/users`, value);
  }

  adminUpdateUser(userId: number, value: UserDto): Observable<any> {
    return this.httpClient.put<ApiResponse>(`${environment.backendUrl}/api/admin/v1/users/${userId}`, value);
  }

  adminDeleteUser(userId: number): Observable<any> {
    return this.httpClient.delete<ApiResponse>(`${environment.backendUrl}/api/admin/v1/users/${userId}`);
  }

  operatorCreateUser(value: any): Observable<UserDto> {
    return this.httpClient.post<UserDto>(`${environment.backendUrl}/api/operator/v1/users`, value);
  }

  operatorUpdateUser(userId: number, value: UserDto): Observable<any> {
    return this.httpClient.put<ApiResponse>(`${environment.backendUrl}/api/operator/v1/users/${userId}`, value);
  }

  operatorDeleteUser(userId: number): Observable<any> {
    return this.httpClient.delete<ApiResponse>(`${environment.backendUrl}/api/operator/v1/users/${userId}`);
  }

  // user overview

  adminGetUserOverview(organisationId: number): Observable<UserOverviewByOrganisationDto> {
    return this.httpClient.get<UserOverviewByOrganisationDto>(
      `${environment.backendUrl}/api/admin/v1/user-organisation-data/organisation/${organisationId}`,
    );
  }

  operatorGetUserOverview(): Observable<UserOverviewByOrganisationDto> {
    return this.httpClient.get<UserOverviewByOrganisationDto>(
      `${environment.backendUrl}/api/operator/v1/user-organisation-data`,
    );
  }

  // regenerate password

  adminRegeneratePassword(userId: number): Observable<any> {
    return this.httpClient.post<ApiResponse>(
      `${environment.backendUrl}/api/admin/v1/users/${userId}/regenerate-password`,
      {},
    );
  }

  operatorRegeneratePassword(userId: number): Observable<any> {
    return this.httpClient.post<ApiResponse>(
      `${environment.backendUrl}/api/operator/v1/users/${userId}/regenerate-password`,
      {},
    );
  }

  // users with permission
  usersWithPermissions(
    data: OrgUnitsWithPermissionsWrite,
    pageNumber: number,
    pageSize: number,
  ): Observable<PaginatedData<UserDto>> {
    return this.httpClient.post<PaginatedData<UserDto>>(
      `${environment.backendUrl}/api/operator/v1/users/with-permissions?pageNumber=${pageNumber}&pageSize=${pageSize}`,
      data,
    );
  }
}
